import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar, Eye, EyeOff, LayoutGrid } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { produksjonAPI, Produksjon } from '../services/api';
import NyProduksjonDialog from '../components/production/NyProduksjonDialog';
import ProductionCard from '../components/production/ProductionCard';
import AppHeader from '../components/layout/AppHeader';
import InfoPopover from '../components/common/InfoPopover';
import { useAuth } from '../contexts/AuthContext';

type PublisertFilter = 'alle' | 'publisert' | 'upublisert';
type TidsFilter = 'fremtidige' | 'tidligere';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin, user } = useAuth();
  
  const [produksjoner, setProduksjoner] = useState<Produksjon[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Brukere skal kun se publiserte produksjoner
  const [publisertFilter, setPublisertFilter] = useState<PublisertFilter>(
    isAdmin() ? 'alle' : 'publisert'
  );
  const [tidsFilter, setTidsFilter] = useState<TidsFilter>('fremtidige');
  const [nyProduksjonOpen, setNyProduksjonOpen] = useState(
    () => searchParams.get('nyProduksjon') === '1'
  );

  useEffect(() => {
    if (searchParams.get('nyProduksjon') === '1') {
      setNyProduksjonOpen(true);
    }
  }, [searchParams]);

  const setNyProduksjonParam = (shouldOpen: boolean) => {
    const next = new URLSearchParams(searchParams);
    if (shouldOpen) {
      next.set('nyProduksjon', '1');
    } else {
      next.delete('nyProduksjon');
    }
    setSearchParams(next, { replace: true });
  };

  const closeNyProduksjonDialog = () => {
    setNyProduksjonOpen(false);
    if (searchParams.get('nyProduksjon')) {
      setNyProduksjonParam(false);
    }
  };

  // Oppdater filter når admin-status endres
  useEffect(() => {
    if (!isAdmin() && publisertFilter !== 'publisert') {
      setPublisertFilter('publisert');
    }
  }, [user, publisertFilter, isAdmin]);

  useEffect(() => {
    const fetchProduksjoner = async () => {
      try {
        // Ved filterendring (ikke første lasting): vis subtle filtering state
        if (!initialLoading) {
          setIsFiltering(true);
        }
        setError(null);
        
        // Bygg filter-objekt basert på valgte filtre
        const filterParams: any = {};
        
        // Tidsfilter
        if (tidsFilter === 'fremtidige') {
          filterParams.kommende = true;
        } else {
          filterParams.gjennomfort = true;
        }
        
        // Publisert-filter
        // Brukere skal alltid kun se publiserte produksjoner
        if (isAdmin()) {
          // Admin kan velge filter
          if (publisertFilter === 'publisert') {
            filterParams.publisert = true;
          } else if (publisertFilter === 'upublisert') {
            filterParams.publisert = false;
          }
          // Hvis 'alle' er valgt, sender vi ikke publisert-parameter
        } else {
          // Brukere ser kun publiserte produksjoner
          filterParams.publisert = true;
        }
        
        // Hent produksjoner med filtre
        const data = await produksjonAPI.getAll(filterParams);
        setProduksjoner(data.produksjoner);
      } catch (err: any) {
        console.error('Feil ved henting av produksjoner:', err);
        setError('Kunne ikke laste produksjoner. Vennligst prøv igjen.');
      } finally {
        setInitialLoading(false);
        setIsFiltering(false);
      }
    };

    fetchProduksjoner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publisertFilter, tidsFilter]);

  const handleNyProduksjonSuccess = () => {
    // Refetch produksjoner etter vellykket opprettelse
    setInitialLoading(false); // Unngå full loader
    const fetchProduksjoner = async () => {
      try {
        setError(null);
        const filterParams: any = {};
        
        if (tidsFilter === 'fremtidige') {
          filterParams.kommende = true;
        } else {
          filterParams.gjennomfort = true;
        }
        
        // Brukere skal alltid kun se publiserte produksjoner
        if (isAdmin()) {
          if (publisertFilter === 'publisert') {
            filterParams.publisert = true;
          } else if (publisertFilter === 'upublisert') {
            filterParams.publisert = false;
          }
        } else {
          filterParams.publisert = true;
        }
        
        const data = await produksjonAPI.getAll(filterParams);
        setProduksjoner(data.produksjoner);
      } catch (err: any) {
        console.error('Feil ved henting av produksjoner:', err);
        setError('Kunne ikke laste produksjoner. Vennligst prøv igjen.');
      }
    };
    fetchProduksjoner();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      <AppHeader />

      <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6">
        {/* Loading State */}
        {initialLoading && (
          <Card className="shadow-sm overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-primary to-blue-400" />
            <CardContent className="p-4 sm:p-6 space-y-3">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </CardContent>
          </Card>
        )}
        
        {/* Error State */}
        {error && (
          <Alert variant="destructive" className="shadow-sm">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {/* Main Content */}
        {!initialLoading && !error && (
          <Card className="shadow-sm hover:shadow-md transition-shadow overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-primary to-blue-400" />
            <CardContent className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Header with Icon */}
              <div className="flex items-start gap-3 mb-2">
                <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                  <LayoutGrid className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                      Produksjoner
                    </h1>
                    <InfoPopover label="Om produksjonslisten">
                      <p>
                        Lister alle fremtidige og tidligere produksjoner. Admin kan også se
                        ikke-publiserte produksjoner.
                      </p>
                    </InfoPopover>
                  </div>
                </div>
              </div>
              
              {/* Filtre */}
              <div className="space-y-3">
                {/* Tidsfilter */}
                <div className="flex justify-center">
                  <ToggleGroup
                    type="single"
                    value={tidsFilter}
                    onValueChange={(value) => {
                      if (value) setTidsFilter(value as TidsFilter);
                    }}
                    className="gap-1"
                  >
                    <ToggleGroupItem 
                      value="fremtidige" 
                      aria-label="Fremtidige produksjoner"
                      className="text-xs sm:text-sm px-3 sm:px-4"
                    >
                      <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                      Fremtidige
                    </ToggleGroupItem>
                    <ToggleGroupItem 
                      value="tidligere" 
                      aria-label="Tidligere produksjoner"
                      className="text-xs sm:text-sm px-3 sm:px-4"
                    >
                      <Calendar className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                      Tidligere
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
                
                {/* Publisert-filter - kun for admins */}
                {isAdmin() && (
                  <div className="flex justify-center">
                    <ToggleGroup
                      type="single"
                      value={publisertFilter}
                      onValueChange={(value) => {
                        if (value) setPublisertFilter(value as PublisertFilter);
                      }}
                      className="gap-1"
                    >
                      <ToggleGroupItem 
                        value="alle" 
                        aria-label="Alle produksjoner"
                        className="text-xs sm:text-sm px-3 sm:px-4"
                      >
                        Alle
                      </ToggleGroupItem>
                      <ToggleGroupItem 
                        value="publisert" 
                        aria-label="Publiserte produksjoner"
                        className="text-xs sm:text-sm px-3 sm:px-4"
                      >
                        <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                        Publisert
                      </ToggleGroupItem>
                      <ToggleGroupItem 
                        value="upublisert" 
                        aria-label="Upubliserte produksjoner"
                        className="text-xs sm:text-sm px-3 sm:px-4"
                      >
                        <EyeOff className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                        Upublisert
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                )}
              </div>
              
              {/* Produksjonsliste */}
              <div
                className={`space-y-2 sm:space-y-3 transition-opacity duration-200 ${
                  isFiltering ? 'opacity-50 pointer-events-none' : 'opacity-100'
                }`}
              >
                {produksjoner.length === 0 ? (
                  <Alert>
                    <AlertDescription>
                      {(() => {
                        let publisertTekst = 'publiserte ';
                        if (isAdmin()) {
                          if (publisertFilter === 'alle') {
                            publisertTekst = '';
                          } else if (publisertFilter === 'upublisert') {
                            publisertTekst = 'upubliserte ';
                          }
                        }
                        const tidsTekst = tidsFilter === 'fremtidige' ? 'fremtidige' : 'tidligere';
                        return `Ingen ${publisertTekst}${tidsTekst} produksjoner funnet`;
                      })()}
                    </AlertDescription>
                  </Alert>
                ) : (
                  produksjoner.map((produksjon) => (
                    <ProductionCard
                      key={produksjon.id}
                      produksjon={produksjon}
                      onClick={() => navigate(`/produksjon/${produksjon.id}`)}
                    />
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Ny Produksjon Dialog */}
      <NyProduksjonDialog
        open={nyProduksjonOpen}
        onClose={closeNyProduksjonDialog}
        onSuccess={handleNyProduksjonSuccess}
      />
    </div>
  );
};

export default Dashboard;

